import { useMutation, useQuery } from '@tanstack/react-query';
import { axiosGet, axiosPost, axiosPut } from '../api/axios';
import { Pathway } from './useExperiences';
import { ListenerRole } from './usePeers';

export enum CrisisStatus {
  YES = 'yes',
  NO = 'no',
  MAYBE = 'maybe',
}

export enum CrisisState {
  NO_CRISIS = 'no_crisis',
  CONFIRMED = 'confirmed',
  REJECTED = 'rejected',
  PENDING = 'pending',
  ESCALATED = 'escalated',
}

export enum CrisisType {
  EMERGENCY = 'emergency',
  SELFHARM = 'selfharm',
}

export enum ChatRole {
  USER = 'user',
  ASSISTANT = 'assistant',
  SYSTEM = 'system',
}

export enum ConversationIntent {
  CLARIFY = 'clarify',
  SEARCH = 'search',
  VENT = 'vent',
}

export interface Content {
  admin_summary: string;
  area_ids: number[];
  audio_url: string;
  background_tags: string[];
  client_id: number;
  client_image_url: string;
  duration: number;
  excerpt?: string;
  file_path: string;
  id: number;
  is_available: boolean;
  is_favorite: boolean;
  is_listener: boolean;
  listener_role_id: number;
  name: string;
  peer_type: string;
  profile_photo_square_file_url: string;
  rating: number;
  resource_client_id: number;
  resource_id: number;
  resource_image_url: string;
  score: number;
  similarity_score: number;
  tag_names: string[];
  title: string;
  transcription: string;
  video_id: string;
}

export interface UnscoredContent {
  _collection_name: string;
  _id: string;
  admin_summary: any;
  ai_summary: any;
  created_at: string;
  duration: number;
  excerpt: any;
  file_url: string;
  id: number;
  listener_role: ListenerRole;
  listener_role_id: number;
  message_quality: number;
  opensearch_tag_ids?: number[];
  score: number;
  title: string;
  topic_tag_ids: number[];
  total_favorites: number;
  total_playbacks: number;
  transcription: string;
  video_id: any;
}

export interface Resource {
  challenge_areas: number[];
  client_id: number;
  description: string;
  id: number;
  image_url?: string;
  indexed_at: any;
  long_description: string;
  meta_keywords: string;
  name: string;
  notes: string;
  phone_number: string;
  sort_weight: number;
  status: string;
  subject_areas: number[];
  tag_names: string[];
  text_number?: string;
  url: string;
  url_description: string;
  wide_image_url: any;
}

export interface UnscoredResource {
  _collection_name: string;
  _id: string;
  challenge_areas: any[];
  client_id: number;
  description: string;
  id: number;
  image_url: string;
  indexed_at: any;
  long_description: string;
  meta_keywords: string;
  name: string;
  notes: string;
  phone_number: string;
  score: number;
  sort_weight: number;
  status: string;
  subject_areas: any[];
  tag_names: string[];
  text_number: any;
  url: string;
  url_description: any;
  wide_image_url: any;
}
export interface People {
  about_me: string;
  can_take_calls: boolean;
  group_name: any;
  is_available: boolean;
  is_listener: boolean;
  is_peer_active: boolean;
  listener_role_id: number;
  profile_photo_url_square: string;
  score: number;
  state: string;
  user: User;
}

export interface User {
  display_name: string;
  id: number;
  test_user: boolean;
}

export interface UnscoredPeople {
  _collection_name: string;
  _id: string;
  about_me: string;
  id: number;
  listener_role_id: number;
  name: string;
  score: number;
  state: string;
}

export interface PoolSizes {
  content: number;
  people: number;
  provider_plans: number;
  resources: number;
}

export interface ProviderPlan {
  common_uses: string;
  description: string;
  key: string;
  score: number;
  title: string;
}

export interface RecommendedExperienceResponse {
  count: number;
  data: Experience[];
}

export interface Experience {
  admin_summary: string;
  area_ids: number[];
  audio_url: string;
  background_tags: string[];
  client_id?: number;
  client_image_url?: string;
  duration: number;
  excerpt: string;
  id: number;
  is_available: boolean;
  // DEPRECATED, sort of, in favor of experience rating
  is_favorite: boolean;
  is_listener: boolean;
  listener_audio_id: number;
  listener_role_id: number;
  name: string;
  peer_type: string;
  profile_photo_square_file_url: string;
  rating: number;
  resource_client_id?: number;
  resource_id?: number;
  resource_image_url?: string;
  score: number;
  similarity_score: number;
  title: string;
  transcription: string;
  tag_names?: string[];
  video_id?: string;
}

export type Chatbot = {
  created_at: Date;
  id: number;
  listener_role_id: number;
  name: string;
  prompt: string;
  status: 'OFF' | 'ON';
  image_url: string;
  display_name: string;
  welcome_message: string;
};

export type ChatbotResponse = {
  chatbots: Chatbot[];
};

type ChatbotChatResponse = {
  chat_history: ChatbotChatHistory[];
};

export type ChatbotMessageResponse = {
  count: number;
  data: ChatbotChatHistory[];
};

export type ChatbotChatHistory = {
  chatbot_id: number;
  created_at: Date;
  completed_at: Date | null;
  id: number;
  user_id?: number;
  user_display_name?: string | null;
  caller_role_id?: number | null;
  messages: ChatbotChatMessage[];
  updated_at: Date;
  crisis_state: CrisisState;
  crisis_type: CrisisType;
  crisis_escalated: boolean;
  crisis_escalated_type: CrisisType;
  summary?: string;
};

export type ChatbotChat = {
  chatbot_id: number;
  created_at: Date;
  thread_id: number;
  ai_response: string;
  crisis_evaluation_results: CrisisEvaluation;
  crisis_state: CrisisState;
  conversation_intent: ConversationIntent;
  message_history: ChatbotChatMessage[];
  search_results?: Content[] | Experience[] | Resource[] | ProviderPlan[];
};

export type CrisisEvaluation = {
  crisis_status: CrisisStatus;
  crisis_type: CrisisType;
  confidence_score: number;
};

export type ChatbotChatMessage = {
  content: string;
  role: ChatRole;
  crisis_status?: CrisisStatus;
  crisis_type?: CrisisType;
  confidence_score?: number;
  timestamp: Date;
  search_results?: SearchResults;
};

type SearchResults = {
  pathway: Pathway;
  results: Content[] | Experience[] | Resource[] | ProviderPlan[];
};

type ChatbotChatRequest = {
  chatbotId?: number;
  userId?: number;
};

type ChatbotMessageRequest = {
  chatbot_id?: number;
  listener_role_ids?: string;
};

export type GetChatbotsPayload = {
  listener_role_ids?: number;
  status?: string;
};

export type CreateChatbotPayload = {
  listener_role_id: number;
  name: string;
  prompt: string;
  welcome_message: string;
  avatar_file_path: string;
  display_name: string;
};

export type UpdateChatbotPayloadInternal = {
  name: string;
  prompt: string;
  status: string;
  welcome_message: string;
  avatar_file_path: string;
  display_name: string;
};

export type UpdateChatbotPayload = UpdateChatbotPayloadInternal & {
  id: number;
};

const getChatbots = async (payload: GetChatbotsPayload = {}) => {
  const response = await axiosGet('/chatbot/', payload);
  return response.data;
};

const createChatBot = async (payload: CreateChatbotPayload) => {
  const response = await axiosPost('/chatbot', payload);
  return response.data;
};

const getChatbotChat = async ({ chatbotId, userId }: ChatbotChatRequest): Promise<ChatbotChatResponse> => {
  return await axiosGet(`/chatbot/${chatbotId}/history/${userId}`, {}).then((response) => response.data);
};

const updateChatBot = async (payload: UpdateChatbotPayloadInternal, id: number) => {
  const response = await axiosPut(`/chatbot/${id}`, payload);
  return response.data;
};

const getChatbotMessage = async (params: ChatbotMessageRequest): Promise<ChatbotMessageResponse> => {
  return await axiosGet(`/chatbot/messages`, params).then((response) => response.data);
};

export const useChatbots = (payload: GetChatbotsPayload = {}) => {
  return useQuery({
    queryKey: ['chatbots', payload],
    queryFn: () => getChatbots(payload),
  });
};

export const useChatbotChat = (params: ChatbotChatRequest) => {
  return useQuery<ChatbotChatResponse>({
    queryKey: ['chatbot-chat', params.chatbotId, params.userId],
    queryFn: () => getChatbotChat(params),
    refetchOnWindowFocus: false,
    enabled: params.chatbotId !== undefined && params.userId !== undefined,
  });
};

export const useCreateChatBot = () => {
  return useMutation({
    mutationFn: (payload: CreateChatbotPayload) => createChatBot(payload),
  });
};

export const useUpdateChatBot = () => {
  return useMutation({
    mutationFn: (payload: UpdateChatbotPayload) => {
      const { id, ...rest } = payload;
      return updateChatBot(rest, id);
    },
  });
};

export const useChatbotMessages = (params: ChatbotMessageRequest) => {
  return useQuery<ChatbotMessageResponse>({
    queryKey: ['chatbot/messages', params.chatbot_id, params.listener_role_ids],
    queryFn: () => getChatbotMessage(params),
    refetchOnWindowFocus: false,
  });
};
